import { useId, useState, type FormEvent } from "react";
import type { ItemProps } from "@bedrock/domain";
import { Button } from "@bedrock/ui";
import { DatePicker, TimePicker } from "./date-time-picker";

export type DateEventProps = Extract<ItemProps, { kind: "date-event" }>;

interface DateEventPresentation {
  day: string;
  month: string;
  weekday: string;
  year: string;
  longDate: string;
}

export function isValidEventDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(year, month - 1, day, 12);
  return candidate.getFullYear() === year && candidate.getMonth() === month - 1 && candidate.getDate() === day;
}

export function isValidEventTime(value: string | undefined): boolean {
  return typeof value === "string" && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function localDate(value: string): Date | null {
  if (!isValidEventDate(value)) return null;
  const [year = 0, month = 1, day = 1] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12);
}

export function formatDateEvent(value: string, locale?: string): DateEventPresentation {
  const date = localDate(value);
  if (!date) return { day: "—", month: "DATE", weekday: "Unscheduled", year: "", longDate: "Unscheduled" };
  return {
    day: new Intl.DateTimeFormat(locale, { day: "2-digit" }).format(date),
    month: new Intl.DateTimeFormat(locale, { month: "short" }).format(date).toLocaleUpperCase(locale),
    weekday: new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date),
    year: new Intl.DateTimeFormat(locale, { year: "numeric" }).format(date),
    longDate: new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(date)
  };
}

export function formatEventTime(value: string | undefined, locale?: string): string {
  if (!value || !isValidEventTime(value)) return "Time not set";
  const [hours = 0, minutes = 0] = value.split(":").map(Number);
  const date = new Date(2000, 0, 1, hours, minutes);
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(date);
}

export function defaultDateEventValues(now = new Date()): { date: string; time: string } {
  const nextSlot = new Date(now);
  nextSlot.setSeconds(0, 0);
  nextSlot.setMinutes((Math.floor(nextSlot.getMinutes() / 30) + 1) * 30);
  const year = nextSlot.getFullYear();
  const month = String(nextSlot.getMonth() + 1).padStart(2, "0");
  const day = String(nextSlot.getDate()).padStart(2, "0");
  const hours = String(nextSlot.getHours()).padStart(2, "0");
  const minutes = String(nextSlot.getMinutes()).padStart(2, "0");
  return { date: `${year}-${month}-${day}`, time: `${hours}:${minutes}` };
}

export function CanvasDateEvent({ props }: { props: DateEventProps }) {
  const date = formatDateEvent(props.date);
  const hasTime = props.time !== undefined && isValidEventTime(props.time);
  const time = hasTime ? formatEventTime(props.time) : "Date only";
  const color = props.color ?? "lilac";
  return <div className={`item-date-event date-event-color--${color}`} role="group" aria-label={`${props.title}, ${date.longDate}${hasTime ? ` at ${time}` : ", no time"}`}>
    <span className="item-date-event__kicker">EVENT · {date.year}</span>
    <div className="item-date-event__date" aria-hidden="true">
      <strong>{date.day}</strong>
      <span><b>{date.month}</b><small>{date.weekday}</small></span>
    </div>
    <h2>{props.title}</h2>
    <time className={hasTime ? undefined : "is-date-only"} dateTime={hasTime ? `${props.date}T${props.time}` : props.date}><span aria-hidden="true"/>{time}</time>
  </div>;
}

export function DateEventForm({ initialValue, submitLabel, onSubmit, onCancel }: {
  initialValue?: DateEventProps | undefined;
  submitLabel: string;
  onSubmit: (props: DateEventProps) => Promise<void>;
  onCancel: () => void;
}) {
  const defaults = defaultDateEventValues();
  const formId = useId();
  const [title, setTitle] = useState(initialValue?.title ?? "");
  const [date, setDate] = useState(initialValue?.date ?? defaults.date);
  const [time, setTime] = useState(initialValue ? initialValue.time ?? "" : defaults.time);
  const color = initialValue?.color ?? "lilac";
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextTitle = title.trim();
    if (!nextTitle) {
      setError("Add a name for this event.");
      return;
    }
    if (!isValidEventDate(date)) {
      setError("Choose a valid date.");
      return;
    }
    if (time && !isValidEventTime(time)) {
      setError("Choose a valid time or leave it blank.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await onSubmit({ kind: "date-event", title: nextTitle, date, color, ...(time ? { time } : {}) });
    } catch {
      setError("The event could not be saved. Try again.");
      setSubmitting(false);
    }
  };

  return <form className="date-event-form" aria-busy={submitting} noValidate onSubmit={(event) => void submit(event)}>
    <div className={`date-event-form__intro date-event-color--${color}`}>
      <span aria-hidden="true">{formatDateEvent(date).day}</span>
      <div><strong>{initialValue ? "Adjust the appointment" : "Mark the moment"}</strong><p>Name the event, choose a date, then add a time if needed.</p></div>
    </div>
    <div className="date-event-form__fields">
      <label htmlFor={`${formId}-title`}><span>Event name</span><input id={`${formId}-title`} autoFocus disabled={submitting} required type="text" maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Dinner with Alex"/></label>
      <div className="date-event-form__picker-row">
        <div className="date-event-form__field"><span>Date</span><DatePicker disabled={submitting} value={date} color={color} onChange={setDate}/></div>
        <div className="date-event-form__field"><span>Time · optional</span><TimePicker disabled={submitting} value={time} color={color} onChange={setTime}/></div>
      </div>
      {error && <p className="date-event-form__error" role="alert">{error}</p>}
    </div>
    <footer><Button type="button" disabled={submitting} onClick={onCancel}>Cancel</Button><Button type="submit" tone="primary" disabled={submitting}>{submitting ? "Saving…" : submitLabel}</Button></footer>
  </form>;
}
